import React from 'react'
import { Input } from '../../../../shared/ui/controls/Input/Input'
import { FieldWrapper } from '../../../../shared/ui'
import { FilterBar } from '../../../../shared/ui/layouts/FilterBar'
import { MultiSelectDropdown } from '../../../../shared/ui/controls/MultiSelectDropdown'
import { GewerkSearchSelect } from 'src/entities/gewerk'
import { HandwerkerSearchSelect } from 'src/entities/user'
import { Gewerk } from '@myapp/contract'
import {
  useControllingFiltersParams,
  toControllingFiltersForm,
  ControllingFiltersForm,
  ControllingSortField,
  ControllingSortDirection,
  ScheduleStatus,
  DeviationStatus,
  ControllingReasonFilterValue,
} from '../../../../features/controlling/filters'
import {
  SCHEDULE_STATUS_OPTIONS,
  DEVIATION_STATUS_OPTIONS,
  REASON_FILTER_OPTIONS,
} from '../model/constants'

interface ControllingFilterBarProps {
  onTechnicianLabelChange?: (label: string | null) => void
  onTradeLabelChange?: (label: string | null) => void
  onTechnicianTradeLabelChange?: (label: string | null) => void
}

export const ControllingFilterBar: React.FC<ControllingFilterBarProps> = ({
  onTechnicianLabelChange,
  onTradeLabelChange,
  onTechnicianTradeLabelChange,
}) => {
  const [params, setParams] = useControllingFiltersParams()
  const filters = toControllingFiltersForm(params)

  const updateField = (field: keyof ControllingFiltersForm, value: string | number | null) => {
    void setParams({
      ...filters,
      [field]: value == null ? '' : String(value),
    })
  }

  const handleSortChange = (
    field: ControllingSortField,
    direction: ControllingSortDirection,
  ) => {
    void setParams({ ...filters, sortField: field, sortDirection: direction })
  }

  const makeSortProps = (sortKey: ControllingSortField) => ({
    sortKey,
    activeSortField: filters.sortField,
    sortOrder: filters.sortDirection,
    onSortChange: (k: string, d: string) =>
      handleSortChange(k as ControllingSortField, d as ControllingSortDirection),
  })

  return (
    <FilterBar minColWidth="160px" gap={12} rowGap={8}>
            {/* Zeitraum */}
            <FieldWrapper label="Zeitraum" className='col-span-2'>
              <div className="flex gap-1 items-center w-full">
                <Input
                  as="input"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(v) => void setParams({ ...filters, dateFrom: v })}
                  placeholder=""
                  className="flex-1 min-w-0"
                />
                <span className="shrink-0">-</span>
                <Input
                  as="input"
                  type="date"
                  value={filters.dateTo}
                  onChange={(v) => void setParams({ ...filters, dateTo: v })}
                  placeholder=""
                  className="flex-1 min-w-0"
                />
              </div>
            </FieldWrapper>

            {/* Auftrags-ID */}
            <FieldWrapper label="Auftrags-ID" {...makeSortProps('workOrderId')}>
              <Input
                as="input"
                value={filters.workOrderId}
                onChange={(v) => void setParams({ ...filters, workOrderId: v })}
                placeholder=""
              />
            </FieldWrapper>

            {/* Handwerker */}
            <FieldWrapper label="Handwerker" {...makeSortProps('technician')}>
              <HandwerkerSearchSelect
                value={filters.technicianId ? Number(filters.technicianId) : null}
                onChange={(id) => updateField('technicianId', id)}
                onLabelChange={onTechnicianLabelChange}
              />
            </FieldWrapper>

            {/* Mieter */}
            <FieldWrapper label="Mieter" {...makeSortProps('tenant')}>
              <Input
                as="input"
                value={filters.tenant}
                onChange={(v) => void setParams({ ...filters, tenant: v })}
                placeholder=""
              />
            </FieldWrapper>

            {/* VE */}
            <FieldWrapper label="VE" {...makeSortProps('ve')}>
              <Input
                as="input"
                value={filters.ve}
                onChange={(v) => void setParams({ ...filters, ve: v })}
                placeholder=""
              />
            </FieldWrapper>

            {/* Gebäude */}
            <FieldWrapper label="Gebäude" {...makeSortProps('building')}>
              <Input
                as="input"
                value={filters.building}
                onChange={(v) => void setParams({ ...filters, building: v })}
                placeholder=""
              />
            </FieldWrapper>

            {/* Terminstatus */}
            <FieldWrapper label="Terminstatus">
              <MultiSelectDropdown<ScheduleStatus>
                value={filters.scheduleStatus}
                onChange={(v) => void setParams({ ...filters, scheduleStatus: v })}
                options={SCHEDULE_STATUS_OPTIONS}
                placeholder="Alle"
              />
            </FieldWrapper>

            {/* Grund */}
            <FieldWrapper label="Grund">
              <MultiSelectDropdown<ControllingReasonFilterValue>
                value={filters.reasons}
                onChange={(v) => void setParams({ ...filters, reasons: v })}
                options={REASON_FILTER_OPTIONS}
                placeholder="Alle"
              />
            </FieldWrapper>

            {/* Auftrag Gewerk (work order's trade from Immotion) */}
            <FieldWrapper label="Auftrag Gewerk" {...makeSortProps('trade')}>
              <GewerkSearchSelect
                value={filters.tradeId ? Number(filters.tradeId) : null}
                onChange={(id, item?: Gewerk) => {
                  updateField('tradeId', id)
                  onTradeLabelChange?.(item ? item.text : null)
                }}
              />
            </FieldWrapper>

            {/* Handwerker Gewerk (technician's profile trades) */}
            <FieldWrapper label="Handwerker Gewerk">
              <GewerkSearchSelect
                value={filters.technicianTradeId ? Number(filters.technicianTradeId) : null}
                onChange={(id, item?: Gewerk) => {
                  updateField('technicianTradeId', id)
                  onTechnicianTradeLabelChange?.(item ? item.text : null)
                }}
              />
            </FieldWrapper>

            {/* Soll-Ist-Status — grouped with numeric/deviation controls */}
            <FieldWrapper label="Soll-Ist-Status">
              <MultiSelectDropdown<DeviationStatus>
                value={filters.deviationStatus}
                onChange={(v) => void setParams({ ...filters, deviationStatus: v })}
                options={DEVIATION_STATUS_OPTIONS}
                placeholder="Alle"
              />
            </FieldWrapper>

            {/* Fahrt Ist min/max */}
            <FieldWrapper label="Fahrt Ist (Std)" {...makeSortProps('actualTravelHours')}>
              <div className="flex gap-1">
                <Input
                  as="input"
                  type="number"
                  step="0.1"
                  value={filters.actualTravelHoursMin}
                  onChange={(v) => void setParams({ ...filters, actualTravelHoursMin: v })}
                  placeholder="min"
                />
                <Input
                  as="input"
                  type="number"
                  step="0.1"
                  value={filters.actualTravelHoursMax}
                  onChange={(v) => void setParams({ ...filters, actualTravelHoursMax: v })}
                  placeholder="max"
                />
              </div>
            </FieldWrapper>

            {/* Arbeit Ist min/max */}
            <FieldWrapper label="Arbeit Ist (Std)" {...makeSortProps('actualWorkHours')}>
              <div className="flex gap-1">
                <Input
                  as="input"
                  type="number"
                  step="0.1"
                  value={filters.actualWorkHoursMin}
                  onChange={(v) => void setParams({ ...filters, actualWorkHoursMin: v })}
                  placeholder="min"
                />
                <Input
                  as="input"
                  type="number"
                  step="0.1"
                  value={filters.actualWorkHoursMax}
                  onChange={(v) => void setParams({ ...filters, actualWorkHoursMax: v })}
                  placeholder="max"
                />
              </div>
            </FieldWrapper>

            {/* Gesamt Ist min/max */}
            <FieldWrapper label="Gesamt Ist (Std)" {...makeSortProps('actualTotalHours')}>
              <div className="flex gap-1">
                <Input
                  as="input"
                  type="number"
                  step="0.1"
                  value={filters.actualTotalHoursMin}
                  onChange={(v) => void setParams({ ...filters, actualTotalHoursMin: v })}
                  placeholder="min"
                />
                <Input
                  as="input"
                  type="number"
                  step="0.1"
                  value={filters.actualTotalHoursMax}
                  onChange={(v) => void setParams({ ...filters, actualTotalHoursMax: v })}
                  placeholder="max"
                />
              </div>
            </FieldWrapper>

            {/* Abweichung % min/max */}
            <FieldWrapper label="Abweichung %" {...makeSortProps('deviation')}>
              <div className="flex gap-1">
                <Input
                  as="input"
                  type="number"
                  value={filters.deviationMin}
                  onChange={(v) => void setParams({ ...filters, deviationMin: v })}
                  placeholder="min"
                />
                <Input
                  as="input"
                  type="number"
                  value={filters.deviationMax}
                  onChange={(v) => void setParams({ ...filters, deviationMax: v })}
                  placeholder="max"
                />
              </div>
            </FieldWrapper>
    </FilterBar>
  )
}
